import { MusicXmlUtil } from "../audio/MusicXmlUtil.js";
import { Sound } from "../audio/Sound.js";
import { IData, VOConfig, VOEmoji, VOEmojiGroup, VOInstrument, VOMelody, VOMelodyNote, VOMelodyTrack } from "./DataStructure.js";

export interface DataOptions {
    assetsUrl: string;
    url: string;
    audioContext: AudioContext;
    progressHandler?: (p: e5.LoaderProgress) => void;
}

export class Data {

    public data: IData;
    public config: VOConfig;

    private _options: DataOptions;
    private _musicXmlUtil: MusicXmlUtil;
    private _warnings: string[] = [];

    constructor(options: DataOptions) {
        this._options = e5.object.merge({

        }, options);

        e5.Debug.add({
            tooltip: "Minimum data",
            uid: "signum-min-data",
            toggle: true,
            reloadAfterToggle: true,
            localStorage: true,
            icon: "fal fa-arrows-to-dot"
        });

        this._musicXmlUtil = new MusicXmlUtil({
            assetsUrl: options.assetsUrl
        });
    }

    public async load(): Promise<IData> {
        const data: IData = await e5.load("json",
            this._options.url, {
            onProgress: this._options.progressHandler
        });

        if (e5.Debug.get("signum-min-data")?.active) {
            data.signum_emoji = [data.signum_emoji[0]];
        }

        const configTitle = e5.Url.params.get("config", "Default");
        this.config = data.signum_config?.find((v) => {
            return configTitle.toLocaleLowerCase() === v.title.toLocaleLowerCase();
        });
        if (!this.config) {
            let message = `Konfiguration mit dem Titel "${configTitle}" nicht vorhanden.`;
            if (data.signum_config.length > 0) {
                message += `<br><br>Folgende Konfigurationen sind vorhanden:<br><div>${data.signum_config.map(
                    (c) => `<div><a href="${e5.Url.params.set("config", c.title)}">${c.title}</a></div>`).join("")}`;
            }
            this.warn(message);
            throw new Error(message);
        }

        this.data = data;

        for (let c of this.data.signum_config) {

        }

        let promises: Promise<any>[] = [];

        for (let instrument of this.data.signum_instrument) {
            promises.push(
                this.loadInstrument(instrument)
            );
        }

        for (let group of this.data.signum_emoji_group) {
            group.emojis = [];
        }

        for (let emoji of this.data.signum_emoji) {
            if (emoji.active === undefined) {
                emoji.active = true;
            }
        }

        this.data.signum_emoji = this.data.signum_emoji.filter((emoji) => {
            return emoji.active;
        });

        // this.data.signum_emoji_group.sort((a, b) => (a.label.localeCompare(b.label)));

        // const emojis = this.data.signum_emoji;

        const defaultGroup: VOEmojiGroup = {
            id: 0,
            icon: "",
            label: "Alle",
            title: "Alle",
            emojis: []
        };
        this.data.signum_emoji_group.unshift(defaultGroup);

        for (let emoji of this.data.signum_emoji) {
            emoji.notesCount = 0;

            emoji.debugName = `Emoji "${emoji.title}" [${emoji.id}].`;

            emoji.groups = e5.array.resolve(
                emoji.groups as any,
                this.data.signum_emoji_group
            );
            emoji.groups.push(defaultGroup);
            for (let group of emoji.groups) {
                group.emojis.push(emoji);
            }

            if (!emoji.melody_kind) {
                emoji.melody_kind = "midi";
            }

            promises.push(this.loadLottie(emoji));
            promises.push(this.loadMelody(emoji));
            promises.push(this.loadMusicSheet(emoji));
        }

        this.data.signum_emoji_group = this.data.signum_emoji_group.filter((g) => g.emojis.length > 0);

        await Promise.all(promises);

        return data;
    }

    private async loadLottie(emoji: VOEmoji): Promise<any> {
        let data = await e5.load("json", emoji.lottie.url);
        emoji.lottieData = data;
        return data;
    }

    private async loadInstrument(instrument: VOInstrument): Promise<VOInstrument> {

        let promises: Promise<any>[] = [];

        for (let tone of instrument.tones) {
            tone.sound = new Sound({
                context: this._options.audioContext
            });
            promises.push(tone.sound.load(tone.tone.url));
        }

        await Promise.all(promises);

        return instrument;
    }

    private async loadMelody(emoji: VOEmoji): Promise<VOMelody> {
        const melody: VOMelody = {
            kind: emoji.melody_kind,
            name: emoji.name,
            duration: 0,
            sound: null,
            tracks: [],
            bpm: 120,
            noteCount: 0
        };
        emoji.melody = melody;

        if (emoji.melody_kind === "audio") {
            melody.sound = new Sound({
                context: this._options.audioContext
            });
            await melody.sound.load(emoji.audio.url);
            melody.duration = melody.sound.buffer.duration;
        }
        else if (emoji.melody_kind === "silence") {
            melody.duration = emoji.silence_duration;
        }
        else if (emoji.melody_kind === "midi") {
            const midiFile: MidiFormat = await this.loadMidiRaw(emoji.midi.url, emoji.debugName);

            melody.bpm = midiFile.header.tempos[0].bpm;

            let duration = 0;

            for (let track of midiFile.tracks) {

                if (track.notes.length === 0) {
                    continue;
                }

                const instrument: VOInstrument = this.getInstrumentForTrack(track);
                if (!instrument) {
                    console.log(emoji.debugName, midiFile.tracks);
                    this.warn(`Could not find instrument for track name "${track.name}". ${emoji.debugName}`);
                    continue;
                }

                const mTrack: VOMelodyTrack = {
                    notes: [],
                    emoji: emoji,
                    melody: melody,
                    instrument: instrument,
                    index: melody.tracks.length
                };

                let noteIndex = 0;

                for (let note of track.notes) {
                    emoji.notesCount++;

                    duration = Math.max(duration, note.time + note.duration + 0.1);
                    // duration = Math.max(duration, note.time + note.duration + 0.05);
                    // duration = Math.max(duration, note.time + note.duration);

                    let mNote: VOMelodyNote = {
                        dur: note.duration,
                        name: note.name,
                        time: note.time,
                        index: noteIndex++,
                        track: mTrack,
                        midi: note.midi,
                        velocity: note.velocity
                    };

                    melody.noteCount++;

                    mTrack.notes.push(mNote);
                }

                melody.tracks.push(mTrack);
            }


            melody.duration = duration;

            if (melody.tracks.length === 0) {
                melody.duration = 1;
                this.warn(`MIDI files without matching track names. Emoji: ${emoji.title} [${emoji.id}].`);
            }


        }

        return melody;
    }

    private async loadMusicSheet(emoji: VOEmoji) {

        let midiUrl: string = null;
        if (emoji.sheet_kind === "auto") {
            if (emoji.melody_kind === "midi") {
                midiUrl = emoji.midi?.url;
            } else if (emoji.melody_kind === "silence") {
                emoji.musicXmlString = await this._musicXmlUtil.normalize(
                    this._options.assetsUrl + "/music-xml/empty.musicxml",
                    emoji.debugName);
                return;
            }
        } else if (emoji.sheet_kind === "midi") {
            midiUrl = emoji.sheet_midi?.url;
        }
        else if (emoji.sheet_kind === "music_xml") {
            emoji.musicXmlString = await this._musicXmlUtil.normalize(
                emoji.sheet_music_xml.url,
                emoji.debugName);
            return;
        }

        if (!midiUrl) {
            this.warn(`Could not find source for music sheet. Using empty music sheet instead. ${emoji.debugName}`);
            emoji.musicXmlString = await this._musicXmlUtil.normalize(
                this._options.assetsUrl + "/music-xml/empty.musicxml",
                emoji.debugName);
            return;
        }


        const midiFile: MidiFormat = await this.loadMidiRaw(midiUrl, emoji.debugName);

        const midiFileTracks: MidiTrack[] = [
            null,//Violin + Pizzicato Strings
            null,//Violin + Pizzicato Strings
            null,//Viola
            null//Cello
        ];

        const midiFileTracksNames: string[][] = [
            ["Violin", "Pizzicato Strings"],
            ["Violin", "Pizzicato Strings"],
            ["Viola"],
            ["Cello"]
        ];

        for (let track of midiFile.tracks) {
            const instrument: VOInstrument = this.getInstrumentForTrack(track);
            if (!instrument) {
                // console.warn(`Could not find instrument for track name "${track.name}". Emoji: ${emoji.title}.`);
                continue;
            }

            for (let j = 0; j < midiFileTracksNames.length; j++) {
                if (midiFileTracks[j] !== null) {
                    continue;
                }
                const trackNamePossible = midiFileTracksNames[j];
                for (let trackName of trackNamePossible) {
                    if (trackName.toLowerCase() === instrument.title.toLowerCase()) {
                        midiFileTracks[j] = track;
                        break;
                    }
                }
            }
        }

        /**
         * Fillup empty tracks with default track data.
         */
        let countFilledTracks = 0;
        for (let i = 0; i < midiFileTracks.length; i++) {
            if (!midiFileTracks[i]) {
                midiFileTracks[i] = {
                    channel: 1,
                    controlChanges: null,
                    endOfTrackTicks: 1,
                    instrument: null,
                    name: "",
                    duration: 0,
                    durationTicks: 0,
                    notes: []
                };
            } else {
                countFilledTracks++;
            }
        }

        if (!countFilledTracks) {
            this.warn(`Could not find matching track names in MIDI file for music sheet. Using empty music sheet instead.`
                + ` ${emoji.debugName}`);
            emoji.musicXmlString = await this._musicXmlUtil.normalize(
                this._options.assetsUrl + "/music-xml/empty.musicxml",
                emoji.debugName);
            return;
        }

        midiFile.tracks = midiFileTracks;
        emoji.musicXmlString = this._musicXmlUtil.fromMidi(midiFile, emoji.debugName);
    }

    private async loadMidiRaw(url: string, debugName: string) {
        let f = await MidiLoader.fromUrl(url);

        /**
         * Instrument number to track-name.
         * @see https://en.wikipedia.org/wiki/General_MIDI
         */
        let instrumentNames: { [key: number]: string } = {
            41: "Violin",
            42: "Viola",
            43: "Cello",
            // 44: "Contrabass",
            // 45: "Tremolo Strings",
            46: "Pizzicato Strings",
            // 47: "Orchestral Harp",
            // 48: "Timpani",
            // 49: "String Ensemble 1",
            // 50: "String Ensemble 2",
            // 51: "Synth Strings 1",
            // 52: "Synth Strings 2",
        };

        let lastName = "";
        let nameCount: any = {};
        for (let track of f.tracks) {

            if (!track.name) {
                track.name = lastName;
            } else {
                lastName = track.name;
            }
        }

        f.tracks = f.tracks.filter((track) => {
            return track.notes.length > 0;
        });

        for (let track of f.tracks) {
            if (track.instrument) {
                let num = track.instrument.number + 1;
                // console.log(`"${track.name}"`, track.instrument.family, track.instrument.name, track.instrument.number + 1);

                if (instrumentNames[num]) {
                    if (nameCount[instrumentNames[num]]) {
                        nameCount[instrumentNames[num]]++;
                    } else {
                        nameCount[instrumentNames[num]] = 1;
                    }
                    track.name = instrumentNames[num];// + " " + nameCount[instrumentNames[num]];
                }
            } else {
                this.warn(`Missing instrument for track "${track.name}". ${debugName}`);
            }
        }



        // let usedNames: any = {};
        // for (let track of f.tracks) {
        //     if (usedNames[track.name]) {
        //         this.warn(`Double Trackname: "${track.name}".`
        //             + ` ${debugName}`);
        //     } else {
        //         usedNames[track.name] = true;
        //     }
        // }

        return f;
    }

    private warn(message: string): void {
        console.warn(message);
        this._warnings.push(message);
    }

    public get warnings(): string[] {
        return this._warnings;
    }

    private getInstrumentForTrack(track: MidiTrack): VOInstrument {
        let trackName = track.name.trim().toLowerCase();

        for (let instrument of this.data.signum_instrument) {
            if (!instrument.track_names) {
                instrument.track_names = [];
            }
            for (let iTrackName of instrument.track_names) {
                if (iTrackName.track_name.trim().toLowerCase()
                    == trackName) {
                    return instrument;
                }
            }
        }
        return null;
    }

    public async loadEmojiCanvases(emojis: VOEmoji[]): Promise<any> {
        for (let emoji of emojis) {
            let player = document.createElement("lottie-player") as LottiePlayer;
            player.style.position = "absolute";
            player.style.top = "0";
            player.style.right = "-128px";
            player.style.width = "128px";
            player.style.height = "128px";
            player.style.visibility = "hidden";
            document.body.appendChild(player);

            player.src = emoji.lottieData as any;
            await e5.wait(0);

            emoji.lottieSvg = player.shadowRoot.querySelector("svg") as SVGSVGElement;

            const snap = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(emoji.lottieSvg.outerHTML);

            const can = await e5.load("svgcanvas", snap, {
                scale: {
                    width: 128
                },
                prepareCanvas: (can) => {
                    can.getContext("2d").fillStyle = "#FFF";
                    can.getContext("2d").fillRect(0, 0, can.width, can.height);
                }
            });
            emoji.lottieCanvas = can;

            player.remove();
        }
    }
}